import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MapPin, Navigation, Search, Loader2 } from "lucide-react";
import { useLocation } from "@/context/LocationContext";
import { toast } from "sonner";

export default function LocationModal({ open, onClose }) {
  const { location, setLocation, detectCurrent, detecting, searchAddress, reverseGeocode } = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [denied, setDenied] = useState(false);

  const handleGPS = async () => {
    setDenied(false);
    try {
      const loc = await detectCurrent();
      toast.success(`Location set to ${loc.city || loc.address}`);
      onClose();
    } catch (e) {
      setDenied(true);
      toast.error("Location access is disabled. Please search manually.");
    }
  };

  const handleSearch = async (val) => {
    setQ(val);
    if (val.length < 3) { setResults([]); return; }
    setSearching(true);
    try { setResults(await searchAddress(val)); } catch { /* ignore */ }
    setSearching(false);
  };

  const pick = async (r) => {
    try {
      const g = await reverseGeocode(r.lat, r.lng);
      setLocation({ ...g, address: r.address });
    } catch {
      setLocation({ lat: r.lat, lng: r.lng, address: r.address, city: r.city || "", state: r.state || "", pincode: r.pincode || "", set: true });
    }
    toast.success("Delivery location updated");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="location-modal-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">Please enter your delivery location</DialogTitle>
          <DialogDescription>Detect your location via GPS or search for an area, street or landmark.</DialogDescription>
        </DialogHeader>

        <Button
          onClick={handleGPS}
          data-testid="location-current-gps-btn"
          disabled={detecting}
          className="w-full justify-start gap-3 rounded-xl bg-brand-soft text-brand hover:bg-brand-soft/70 border border-brand/20"
        >
          {detecting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Navigation className="h-5 w-5" />}
          <div className="text-left">
            <div className="font-semibold">Use current location</div>
            <div className="text-xs opacity-70">Detect via GPS</div>
          </div>
        </Button>

        {denied && (
          <div className="rounded-lg bg-amber-soft px-3 py-2 text-xs text-amber-warm" data-testid="location-denied-msg">
            Location access is disabled. Enter your area, street or landmark below.
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="location-search-input"
            value={q}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search area, street or landmark"
            className="rounded-xl pl-9"
          />
          {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-400" />}
        </div>

        <div className="max-h-60 space-y-1 overflow-auto">
          {results.map((r, i) => (
            <button
              key={i}
              data-testid={`location-select-item-${i}`}
              onClick={() => pick(r)}
              className="flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-slate-100"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <div className="text-sm font-medium text-slate-800">{r.address}</div>
                <div className="line-clamp-1 text-xs text-slate-400">{r.full}</div>
              </div>
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-500">
          Current: <span className="font-medium text-slate-700">{location.address}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
